<script setup lang="ts">
import { onBeforeMount, ref } from 'vue';
import apiClient from '@src/api/api-client.js';
import { StockReservation } from '@src/reservation/interfaces/stock-reservation.js';
import { Serial } from '@src/product/interfaces/serial.js';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import Icon from '@components/icons/icon.vue';
import Text from '@components/text.vue';
import PickInfoByStockReservation from '@src/pick/pick-info/pick-info-by-stock-reservation.vue';
import RoleCheckWrapper from '@components/role-check-wrapper.vue';
import Separator from '@components/separator.vue';
import Headline from '@components/headline.vue';
import Card from '@components/card.vue';
import { TooltipColors } from '@components/tooltip-types.ts';
import IconTooltip from '@components/icons/icon-tooltip.vue';
import type { IconColors } from '@components/icons/icon-types.ts';
import ItemLoader from '@components/loader/item-loader.vue';

const props = defineProps({
    serials: Array<string>,
});

interface SerialData {
    serial: Serial
    reservation: StockReservation
}

const serialData = ref<Array<SerialData>>([]);

const loading = ref(false);
onBeforeMount(async () => {
    loading.value = true;
    serialData.value = (await apiClient.post('/be/stock-reservation/details', props.serials)).data;
    loading.value = false;
});

function getSerialTooltipConfig(serialData: SerialData): {
    icon: string,
    iconColor: IconColors,
    color: TooltipColors,
    tooltip: string
} {
    if (serialData.reservation) {
        return {
            icon: 'check',
            color: 'green',
            iconColor: 'green',
            tooltip: 'Reserviert',
        };
    }

    if (!serialData.serial.bin) {
        return {
            icon: 'times',
            color: 'yellow',
            iconColor: 'yellow',
            tooltip: 'Nicht lagernd',
        };
    }

    return {
        icon: 'check',
        color: 'grey',
        iconColor: 'grey',
        tooltip: 'Lagernd',
    };
}
</script>

<template>
    <VerticalAlignChildrenWrapper v-if="!loading" gap-bottom="2">
        <Separator gap="2"/>
        <Headline>Seriennummern:</Headline>
        <VerticalAlignChildrenWrapper gap-bottom="1">
            <Card v-for="data in serialData" :card-color="data.reservation ? 'green' : (data.serial.bin ? 'grey' : 'yellow')">
                <HorizontalAlignChildrenWrapper gap="2" center-children :wrap-on-small-screen="false">
                    <IconTooltip v-bind="getSerialTooltipConfig(data)" hover-icon-color="default"/>
                    <Text bold>{{ data.serial.serial }}</Text>
                    <template v-if="data.serial.bin">
                        <Icon icon="arrow-right"/>
                        <Text :word-break="false">{{ data.serial.bin!.binCode }} ({{ data.serial.bin!.idBin }})</Text>
                    </template>
                </HorizontalAlignChildrenWrapper>
                <RoleCheckWrapper :allowed-roles="['ROLE_PICK_INFO_PICK_QUEUE_BY_RESERVATION']">
                    <PickInfoByStockReservation v-if="data.reservation?.id" :id-stock-reservation="data.reservation.id"/>
                </RoleCheckWrapper>
                <Text v-if="data.serial.manufacturerSerial">Rahmennummer: {{ data.serial.manufacturerSerial }}</Text>
            </Card>
        </VerticalAlignChildrenWrapper>
    </VerticalAlignChildrenWrapper>
    <ItemLoader v-else/>
</template>
